import { getConfig } from '../../config';

const config = getConfig();

// Every internal link uses a trailing slash to match `trailingSlash: 'always'`.
export const links = {
  home: '/',
  signup: `${config.platformUrl}/signup`,
  login: `${config.platformUrl}/login`,
  // Book a demo opens Greg's calendar in a modal (DemoBooking.tsx); the page
  // is the fallback for new tabs and crawlers.
  demo: '/book-a-demo/',
  calendly: 'https://calendly.com/greg-canyou/30min',
  partner: '/book-a-demo/',
  pricing: '/pricing/',
  caseStudies: '/case-studies/',
  fastLabourHire: '/case-studies/fast-labour-hire/',
  blog: '/blog/',
  support: '/support/',
  employeeOnboarding: '/features/employee-onboarding/',
  licences: '/features/licence-and-certification-management/',
  identity: '/features/client-identity-verification/',
  tfn: '/features/tax-file-number-declaration/',
  esign: '/electronic-signature/',
  xero: '/integrations/xero/',
  integrations: '/integrations/',
  partners: '/partners/',
  industries: '/#industries',
  construction: '/industries/construction/',
  labourHire: '/industries/labour-hire/',
  agriculture: '/industries/agriculture/',
  education: '/industries/education/',
  agedCare: '/industries/aged-care/',
  qrSignIn: '/features/qr-site-sign-in/',
  workRights: '/features/work-rights-checks/',
  privacy: '/privacy-policy/',
  terms: '/terms-and-conditions/',
  eula: '/end-user-license-agreement/',
  websiteTerms: '/website-terms-of-use/',
  email: 'mailto:hello@canyou.com.au',
};

export type NavLink = { label: string; href: string; desc?: string };
export type NavGroup = { label: string; href?: string; items?: NavLink[] };

export const navGroups: NavGroup[] = [
  {
    label: 'Product',
    items: [
      {
        label: 'Work-rights checks',
        href: links.workRights,
        desc: 'Check visas with Home Affairs (VEVO)',
      },
      {
        label: 'Licence & certification tracking',
        href: links.licences,
        desc: 'Tickets tracked to expiry, with automatic reminders',
      },
      {
        label: 'Onboarding workflows',
        href: links.employeeOnboarding,
        desc: 'One link to collect everything a new starter needs',
      },
      {
        label: 'Client identity verification',
        href: links.identity,
        desc: 'Verify client IDs without the inbox',
      },
      {
        label: 'TFN declaration',
        href: links.tfn,
        desc: 'TFN, super and bank details, lodged correctly',
      },
      {
        label: 'QR site sign-in',
        href: links.qrSignIn,
        desc: 'Inductions and tickets checked at the gate',
      },
    ],
  },
  {
    label: 'Industries',
    items: [
      {
        label: 'Construction & trades',
        href: links.construction,
        desc: 'Tickets, inductions and subbies checked at the gate',
      },
      {
        label: 'Labour hire & recruitment',
        href: links.labourHire,
        desc: 'Same-day job readiness for every placement',
      },
      {
        label: 'Agriculture',
        href: links.agriculture,
        desc: 'Seasonal crews and visa checks before harvest',
      },
      {
        label: 'Education & training',
        href: links.education,
        desc: 'Clearances and registrations tracked to expiry',
      },
      {
        label: 'Aged care',
        href: links.agedCare,
        desc: 'Worker screening and credentials kept current',
      },
    ],
  },
  { label: 'Pricing', href: links.pricing },
  {
    label: 'Resources',
    items: [
      {
        label: 'Integrations',
        href: links.integrations,
        desc: 'Xero, MYOB, Deputy, ATO super and VEVO',
      },
      {
        label: 'Case studies',
        href: links.caseStudies,
        desc: 'How Fast Labour Hire cut onboarding to 15 minutes',
      },
      { label: 'Blog', href: links.blog, desc: 'Guides and product updates' },
      {
        label: 'Help Centre',
        href: links.support,
        desc: 'Step-by-step help for every feature',
      },
    ],
  },
];

export const footerColumns: { title: string; items: NavLink[] }[] = [
  {
    title: 'Product',
    items: [
      { label: 'Work-rights checks', href: links.workRights },
      { label: 'Licence & cert tracking', href: links.licences },
      { label: 'Onboarding workflows', href: links.employeeOnboarding },
      { label: 'Client identity verification', href: links.identity },
      { label: 'TFN declaration', href: links.tfn },
      { label: 'QR site sign-in', href: links.qrSignIn },
      { label: 'E-sign', href: links.esign },
      { label: 'Integrations', href: links.integrations },
      { label: 'Xero integration', href: links.xero },
    ],
  },
  {
    title: 'Industries',
    items: [
      { label: 'Construction & trades', href: links.construction },
      { label: 'Labour hire & recruitment', href: links.labourHire },
      { label: 'Agriculture', href: links.agriculture },
      { label: 'Education & training', href: links.education },
      { label: 'Aged care', href: links.agedCare },
    ],
  },
  {
    title: 'Resources',
    items: [
      { label: 'Case studies', href: links.caseStudies },
      { label: 'Blog', href: links.blog },
      { label: 'Help Centre', href: links.support },
      { label: 'Pricing', href: links.pricing },
    ],
  },
  {
    title: 'Legal',
    items: [
      { label: 'Privacy policy', href: links.privacy },
      { label: 'Terms & conditions', href: links.terms },
      { label: 'End user licence', href: links.eula },
      { label: 'Website terms of use', href: links.websiteTerms },
      { label: 'Electronic signature', href: links.esign },
    ],
  },
];

export const socials: NavLink[] = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/canyouappau' },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/channel/UCZImavpuI8y9NEIp5xWxECA',
  },
  { label: 'Facebook', href: 'https://www.facebook.com/canyouappau' },
  { label: 'Instagram', href: 'https://www.instagram.com/canyouapp' },
];
